import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  FormField,
  StatusBadge,
  Textarea,
  useToast,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { extractErrorMessage } from "@/api/client";
import {
  buyerAssignmentsApi,
  buyersApi,
  type BuyerLeadStatus,
} from "@/api/buyers";
import {
  buyerActionsApi,
  type BulkBuyerActionOutcome,
} from "@/api/buyerActions";
import {
  buyerAllocationKeys,
  buyerAssignmentKeys,
  buyerKeys,
} from "@/hooks/buyers/buyerKeys";
import { useAuth } from "@/hooks/useAuth";
import { MIN_ARCHIVE_REASON } from "@/pages/buyers/archive/ArchiveBuyerModal";
import { BUYER_STATUSES, buyerLabel } from "@/pages/buyers/buyerFilters";
import { BUYER_TERMINAL_STATUSES } from "@/pages/buyers/buyerPhases";
import { SpecialistCandidateSelect } from "./SpecialistQuickPick";

type BulkAction = "reassign" | "status" | "education" | "archive";

/** One normalised shape for four endpoints that each report per item. */
export interface BulkOutcome {
  succeeded: number;
  failed: number;
  skipped: number;
  items: {
    id: string;
    outcome: BulkBuyerActionOutcome;
    detail?: string | null;
  }[];
}

/** The bulk-assign endpoint says `assigned` where the others say `succeeded`. */
function fromAssign(result: {
  assigned: number;
  skipped: number;
  failed: number;
  items: {
    buyerLeadId: string;
    outcome: "assigned" | "skipped" | "failed";
    detail?: string | null;
  }[];
}): BulkOutcome {
  return {
    succeeded: result.assigned,
    failed: result.failed,
    skipped: result.skipped,
    items: result.items.map((i) => ({
      id: i.buyerLeadId,
      outcome: i.outcome === "assigned" ? "succeeded" : i.outcome,
      detail: i.detail,
    })),
  };
}

/** The status and education endpoints count succeeded/failed; skipped rides
 *  the item list only. */
function fromAction(result: {
  succeeded: number;
  failed: number;
  items: {
    id: string;
    outcome: BulkBuyerActionOutcome;
    detail?: string | null;
  }[];
}): BulkOutcome {
  return {
    succeeded: result.succeeded,
    failed: result.failed,
    skipped: result.items.filter((i) => i.outcome === "skipped").length,
    items: result.items,
  };
}

/** Mirrors the queue page's key: minted once per attempt, reused on a retry,
 *  so a partially-failed bulk assign never double-credits the append-only
 *  grant ledger. */
export function bulkAssignIdempotencyKey(
  specialistUserId: string,
  ids: readonly string[],
): string {
  return `bulk:${specialistUserId}:${[...ids].sort().join(",")}`;
}

export interface BuyerBulkActionsBarProps {
  selectedIds: string[];
  /** Display names for the per-row result list, keyed by id. */
  names: Record<string, string>;
  onClear: () => void;
}

/**
 * The bulk-actions bar over the buyers list, shown while rows are selected.
 *
 * <p>Four verbs — reassign, change status, send the education email, archive —
 * each behind its own confirmation, and each gated on the permission its
 * endpoint enforces: `Buyers.ManageAll` for the two that move a buyer across
 * books (reassign, archive), `Buyers.Update` for the two that act on rows the
 * caller already owns. The server refuses either way; the gate only decides
 * what is offered.</p>
 *
 * <p>🔴 <b>Every result is reported PER ROW, never partial-silent.</b> All four
 * endpoints answer item by item, because a bulk write over other people's
 * books legitimately half-succeeds — one buyer archived, one refused. The
 * dialog therefore stays open on completion and lists what happened to each
 * name ("2 of 3 succeeded; Jane Doe: …"), rather than toasting a headline that
 * the reader would have to take on trust.</p>
 *
 * <p>Selection is PAGE-SCOPED and the bar says so in words — the list is
 * server-paged, so "all" here is one page of the book.</p>
 */
export function BuyerBulkActionsBar({
  selectedIds,
  names,
  onClear,
}: BuyerBulkActionsBarProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManageAll = hasPermission("Buyers.ManageAll");
  const canUpdate = hasPermission("Buyers.Update");

  const [action, setAction] = useState<BulkAction | null>(null);
  const [specialist, setSpecialist] = useState<string | null>(null);
  const [status, setStatus] = useState<BuyerLeadStatus>("Nurture");
  const [reason, setReason] = useState("");
  const [outcome, setOutcome] = useState<BulkOutcome | null>(null);

  const count = selectedIds.length;

  const close = () => {
    setAction(null);
    setSpecialist(null);
    setReason("");
    setOutcome(null);
  };

  const invalidate = () => {
    // The buyer reads (rows, stat cards, details), the assignment queue and the
    // allocation standings — every one of these verbs can move all three.
    qc.invalidateQueries({ queryKey: buyerKeys.all });
    qc.invalidateQueries({ queryKey: buyerAssignmentKeys.all });
    qc.invalidateQueries({ queryKey: buyerAllocationKeys.all });
  };

  const mutation = useMutation({
    mutationFn: async (): Promise<BulkOutcome> => {
      const ids = [...selectedIds];
      const trimmed = reason.trim();
      switch (action) {
        case "reassign":
          return fromAssign(
            await buyerAssignmentsApi.bulkAssign({
              specialistUserId: specialist!,
              buyerLeadIds: ids,
              idempotencyKey: bulkAssignIdempotencyKey(specialist!, ids),
              reason: trimmed || undefined,
            }),
          );
        case "status":
          return fromAction(
            await buyerActionsApi.bulkSetStatus({
              ids,
              status,
              reason: trimmed || null,
            }),
          );
        case "education":
          return fromAction(
            await buyerActionsApi.bulkSendEducationEmail({ ids }),
          );
        case "archive": {
          const r = await buyersApi.bulkArchive({ ids, reason: trimmed });
          const skipped = r.alreadyArchived + r.notFound;
          return {
            succeeded: r.archived,
            failed: 0,
            skipped,
            // A server predating the per-row list still answers with counts;
            // the list then shows the counts and no names.
            items: r.items ?? [],
          };
        }
        default:
          throw new Error("No bulk action chosen.");
      }
    },
    onSuccess: (result) => {
      invalidate();
      setOutcome(result);
      // The selection is spent whichever way the rows went; a retry starts
      // from a fresh pick, with the rows reflecting what actually happened.
      onClear();
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e)),
  });

  // The result dialog outlives the selection: a successful run clears the
  // rows (they are spent), and the report of what happened to them has to
  // stay on screen after that.
  if (count === 0 && !action) return null;

  const reasonOk = reason.trim().length >= MIN_ARCHIVE_REASON;
  // The server refuses a terminal status with no reason on every row
  // (BuyerLeadService.IsTerminal), so the batch would only come back all failed.
  const statusReasonRequired =
    action === "status" && BUYER_TERMINAL_STATUSES.includes(status);
  const submitDisabled =
    mutation.isPending ||
    (action === "reassign" && !specialist) ||
    (action === "archive" && !reasonOk) ||
    (statusReasonRequired && reason.trim().length === 0);

  const title =
    action === "reassign"
      ? `Reassign ${count} buyer${count === 1 ? "" : "s"}`
      : action === "status"
        ? `Change status on ${count} buyer${count === 1 ? "" : "s"}`
        : action === "education"
          ? `Send the education email to ${count} buyer${count === 1 ? "" : "s"}`
          : `Archive ${count} buyer${count === 1 ? "" : "s"}`;

  return (
    <>
      {count > 0 && (
        <div
          className="mb-space-3 flex flex-wrap items-center justify-between gap-space-3 rounded-md border border-border-light bg-white-warm p-space-3"
          data-testid="bulk-actions-bar"
        >
          <span className="text-small text-onyx">
            {count} buyer{count === 1 ? "" : "s"} selected on this page.
          </span>
          <div className="flex flex-wrap items-center gap-space-2">
            <Button variant="text" size="sm" onClick={onClear}>
              Clear selection
            </Button>
            {canManageAll && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setAction("reassign")}
                data-testid="bulk-reassign-open"
              >
                Reassign
              </Button>
            )}
            {canUpdate && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setAction("status")}
                data-testid="bulk-status-open"
              >
                Change status
              </Button>
            )}
            {canUpdate && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setAction("education")}
                data-testid="bulk-education-open"
              >
                Send education email
              </Button>
            )}
            {canManageAll && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setAction("archive")}
                data-testid="bulk-archive-open"
              >
                Archive
              </Button>
            )}
          </div>
        </div>
      )}

      {action && (
        <Modal open onClose={close} title={title} size="md">
          {outcome ? (
            <BulkOutcomeList outcome={outcome} names={names} onDone={close} />
          ) : (
            <form
              className="grid gap-space-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (!submitDisabled) mutation.mutate();
              }}
            >
              {action === "reassign" && (
                <FormField label="Consultant" required>
                  <SpecialistCandidateSelect
                    buyerLeadId={selectedIds[0] ?? null}
                    value={specialist}
                    onChange={setSpecialist}
                    includeUnassignedOption={false}
                    aria-label="Consultant to reassign to"
                  />
                </FormField>
              )}
              {action === "status" && (
                <FormField label="New status" required>
                  <Select
                    value={status}
                    onChange={(e) =>
                      setStatus(e.target.value as BuyerLeadStatus)
                    }
                    data-testid="bulk-status-select"
                  >
                    {BUYER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {buyerLabel(s)}
                      </option>
                    ))}
                  </Select>
                </FormField>
              )}
              {action === "education" && (
                <p className="m-0 text-small text-gray-mid">
                  Each buyer receives the education email with the buyer
                  brochure attached. A buyer with no address, or one it has
                  already been sent to, is reported rather than sent to twice.
                </p>
              )}
              {action === "archive" && (
                <p className="m-0 text-small text-gray-mid">
                  They drop out of the buyers list, the assignment queue and the
                  buyer pickers. Nothing is deleted and every record still
                  opens. Any that are already archived are skipped.
                </p>
              )}
              {action !== "education" && (
                <FormField
                  label={
                    action === "archive"
                      ? "Why archive them?"
                      : statusReasonRequired
                        ? "Reason"
                        : "Reason (optional)"
                  }
                  required={action === "archive" || statusReasonRequired}
                  helperText={
                    action === "archive"
                      ? `At least ${MIN_ARCHIVE_REASON} characters. One reason is recorded on every buyer in this batch.`
                      : statusReasonRequired
                        ? `Required when closing buyers out as ${buyerLabel(status)}. One reason is recorded on every buyer in this batch.`
                        : undefined
                  }
                >
                  <Textarea
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    data-testid="bulk-reason"
                  />
                </FormField>
              )}
              <div className="flex items-center justify-end gap-space-2">
                <Button
                  variant="text"
                  size="sm"
                  type="button"
                  onClick={close}
                  disabled={mutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  type="submit"
                  disabled={submitDisabled}
                  data-testid="bulk-confirm"
                >
                  {mutation.isPending ? "Working…" : `Apply to ${count}`}
                </Button>
              </div>
            </form>
          )}
        </Modal>
      )}
    </>
  );
}

const OUTCOME_TONE = {
  succeeded: "ok",
  failed: "danger",
  skipped: "warn",
} as const;

/** What happened, row by row. */
function BulkOutcomeList({
  outcome,
  names,
  onDone,
}: {
  outcome: BulkOutcome;
  names: Record<string, string>;
  onDone: () => void;
}) {
  const total = outcome.succeeded + outcome.failed + outcome.skipped;
  return (
    <div className="grid gap-space-4" data-testid="bulk-outcome">
      <p className="m-0 text-small text-onyx" role="status">
        {outcome.succeeded} of {total} succeeded
        {outcome.failed > 0 ? `; ${outcome.failed} failed` : ""}
        {outcome.skipped > 0 ? `; ${outcome.skipped} skipped` : ""}.
      </p>
      {outcome.items.length > 0 && (
        <ul className="m-0 grid list-none gap-space-2 p-0">
          {outcome.items.map((item) => (
            <li
              key={item.id}
              className="flex flex-wrap items-center gap-space-2 text-small"
            >
              <StatusBadge tone={OUTCOME_TONE[item.outcome]}>
                {item.outcome}
              </StatusBadge>
              <span className="text-onyx">{names[item.id] ?? item.id}</span>
              {item.detail && (
                <span className="text-gray-mid">— {item.detail}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={onDone}
          data-testid="bulk-done"
        >
          Done
        </Button>
      </div>
    </div>
  );
}
